import {
  formatIsoDate,
  formatName,
  renderStatus,
} from "../../../utils/generalUtils";

import GeneratePdf from "../../../generatePdf/GeneratePdf";
import { Button, Space, Tooltip } from "antd";
import { EditOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import ViewingFormButton from "../../Form/ViewingFormButton";
// import ConfirmDelete from "../../ConfirmDelete/ConfirmDelete";

// Required/Additional Details shown both in the pre-submit review and the
// post-submission preview.
const baseViewingDataConfig = [
  {
    key: 1,
    collapseName: "Required Details",
    collapseItems: [
      { key: 1, name: "submitter_position", label: "Position" },
      { key: 2, name: "start_date", label: "Start Date" },
      { key: 3, name: "end_date", label: "End Date" },
      { key: 4, name: "destination", label: "Destination" },
      { key: 5, name: "purpose", label: "Purpose of Travel" },
    ],
  },
  {
    key: 2,
    collapseName: "Additional Details",
    collapseItems: [
      { key: 1, name: "expenses", label: "Estimated Expenses" },
      { key: 2, name: "funding_source", label: "Funding Source" },
      {
        key: 3,
        name: "local_travel_transportation",
        label: "Local Travel Transportation",
      },
      { key: 4, name: "entitlement", label: "Entitlement" },
    ],
  },
];

export const trlConfig = {
  docType: "Local Travel Request",
  title: "Local Travel Request",
  description:
    "Submit a request to travel locally for official university purposes. Your request will be reviewed by your assigned approver. Once approved, you may download the official travel document.",
  apiConfig: {
    get: "/employee/trl",
    post: "/employee/addtrl",
    update: "/employee/updatetrl",
    cancel: "/employee/cancel/trl",
  },
  dataMapper: (item, index) => ({
    ...item,
    name: `${item?.submission?.submitter?.last_name || ""}, ${
      item?.submission?.submitter?.first_name || ""
    }`.trim(),
    formattedName: formatName(
      item?.submission?.submitter?.first_name,
      item?.submission?.submitter?.middle_name,
      item?.submission?.submitter?.last_name,
      "lfmcut"
    ),
    approver_name: `${item?.submission?.approval?.[0]?.approver?.last_name || ""} ${
      item?.submission?.approval?.[0]?.approver?.first_name || ""
    }`.trim(),
    formattedApproverName: formatName(
      item?.submission?.approval?.[0]?.approver?.first_name,
      item?.submission?.approval?.[0]?.approver?.middle_name,
      item?.submission?.approval?.[0]?.approver?.last_name,
      "lfmcut"
    ),
    submitter_position: item?.submission?.position?.position_name,
    submitter_position_unit: item?.submission?.position?.unit,
    approval_date: item?.submission?.approval?.[0]?.approval_date,
    remarks: item?.submission?.remarks,
    date_submitted: item?.created_at ? formatIsoDate(item?.created_at) : null,
    key: index + 1,
    status: item?.submission?.status,
  }),
  columns: (onDeleted, onEdit) => [
    { title: "No.", dataIndex: "key", rowScope: "row", width: 60 },
    { title: "Destination", dataIndex: "destination", key: "destination" },
    {
      title: "Position",
      dataIndex: "submitter_position",
      key: "submitter_position",
    },
    { title: "Start Date", dataIndex: "start_date", key: "start_date" },
    { title: "End Date", dataIndex: "end_date", key: "end_date" },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: renderStatus(
        ["pending"],
        ["approved"],
        ["disapproved", "denied"],
        ["cancelled"]
      ),
    },
    {
      title: "Action",
      key: "action",
      render: (_, item) => (
        <Space>
          <GeneratePdf
            docuType="trl"
            contents={item}
            disabled={item?.status !== "approved"}
            isEmployee={true}
            isIcon={true}
          />
          <ViewingFormButton
            contents={item}
            viewingDataConfig={trlConfig.postSubmissionViewingDataConfig}
            isIcon={true}
          />
          <Tooltip
            title={
              item?.status === "pending"
                ? "Edit or cancel this submission"
                : "Only pending submissions can be edited"
            }
          >
            <Button
              type="primary"
              shape="circle"
              icon={<EditOutlined />}
              disabled={item?.status !== "pending"}
              onClick={() => onEdit?.(item)}
              style={
                item?.status === "pending"
                  ? {
                      color: "white",
                      backgroundColor: "#FA8C16",
                      borderColor: "#FA8C16",
                    }
                  : {}
              }
            />
          </Tooltip>
          {/* <ConfirmDelete
            isIcon={true}
            rowId={item?.id}
            buttonText="Delete Submission"
            docType="trl"
            onDeleted={onDeleted}
          /> */}
        </Space>
      ),
    },
    {
      title: "Date Submitted",
      dataIndex: "date_submitted",
      key: "date_submitted",
    },
  ],
  // Reverse of the form -> payload mapping — maps a stored submission back
  // into edit-form values. Note: DB column is "funding_sourse" (typo kept
  // for compatibility); the form field is spelled correctly.
  toFormValues: (item) => ({
    date:
      item?.start_date && item?.end_date
        ? [dayjs(item.start_date), dayjs(item.end_date)]
        : undefined,
    start_date: item?.start_date,
    end_date: item?.end_date,
    destination: item?.destination,
    purpose: item?.purpose,
    expenses: item?.expenses,
    funding_source: item?.funding_sourse,
    local_travel_transportation: item?.local_travel_transportation,
    entitlement: item?.entitlement,
  }),
  formFields: [
    {
      name: "date",
      label: "Date",
      type: "daterange",
      // onSameMonth: true,
      rules: [{ required: true, message: "This is required", type: "array" }],
    },
    {
      name: "destination",
      label: "Destination",
      placeholder: "Enter destination",
      rules: [
        { required: true, message: "This is required", whitespace: true },
      ],
    },
    {
      name: "purpose",
      label: "Purpose of Travel",
      placeholder: "Enter purpose of travel",
      rules: [
        { required: true, message: "This is required", whitespace: true },
      ],
    },
    {
      name: "expenses",
      label: "Estimated Expenses",
      placeholder: "Enter estimated expenses",
    },
    {
      name: "funding_source",
      label: "Funding Source",
      placeholder: "Enter funding source",
    },
    {
      name: "local_travel_transportation",
      label: "Local Travel Transportation",
      placeholder: "Enter Local Travel Transportation",
    },
    {
      name: "entitlement",
      label: "Entitlement",
      placeholder: "Enter entitlement",
    },
  ],
  // Used for the pre-submit review modal (PvForm) — no remarks yet at this point.
  viewingDataConfig: baseViewingDataConfig,
  // Used for the post-submission preview (ViewingFormButton) — adds the approver's remarks.
  postSubmissionViewingDataConfig: [
    ...baseViewingDataConfig,
    {
      key: 3,
      collapseName: "Remarks",
      collapseItems: [
        {
          key: 1,
          name: "remarks",
          label: "Remarks",
          render: (val) => val || "None",
        },
      ],
    },
  ],
};
